import { useState } from "react";

import { format } from "date-fns";

import ChevronDown from "@/assets/icons/chevron-down.svg";
import ChevronRight from "@/assets/icons/chevron-right.svg";
import { Source } from "@/constants";
import Cover from "@/containers/Episode/partials/Cover";
import { ChannelDetailEpisode } from "@/types/Channel";
import { truncate } from "@/utils/truncate";
import Table from "@components/Table";

import EpisodeDetails from "../EpisodeDetails";
import EpisodeMentionCount from "../EpisodeMentionCount";
import TranscriptStatusCell from "../TranscriptStatusCell";

import styles from "./EpisodeListTable.module.css";

type EpisodeRowProps = {
  channel_type: Source;
  episode: ChannelDetailEpisode;
  toggleShowDetails: (episode_id: string) => void;
  showDetailsEpisodeID: string | undefined;
};

type EpisodeListTableProps = {
  channel_type: Source;
  episodes: ChannelDetailEpisode[];
};

const EpisodeRow: React.FunctionComponent<EpisodeRowProps> = ({
  channel_type,
  episode,
  toggleShowDetails,
  showDetailsEpisodeID,
}) => {
  return (
    <>
      <tr>
        <td>
          <Cover
            className={styles.cover}
            dumb
            episode={{
              source: channel_type,
              cover: episode.artwork_url,
            }}
          />
        </td>
        <td className={styles.episodeNameColumn}>
          {episode.title}
          <p className={styles.episodeDescription}>
            {truncate(episode.description, 150)}
          </p>
          <div
            className={styles.showDetailsButton}
            onClick={() => toggleShowDetails(episode.id)}
          >
            {showDetailsEpisodeID === episode.id ? (
              <ChevronDown className={styles.showDetailsArrowIcon} />
            ) : (
              <ChevronRight className={styles.showDetailsArrowIcon} />
            )}
            <span>Show episode details</span>
          </div>
        </td>
        <td>
          <div className={styles.dateCell}>
            {episode.published_date
              ? format(new Date(episode.published_date), "PP HH:mm")
              : null}
          </div>
        </td>
        <td>
          <div className={styles.dateCell}>
            {episode.indexed_elasticsearch
              ? format(new Date(episode.indexed_elasticsearch), "PP HH:mm")
              : null}
          </div>
        </td>
        <td>
          <EpisodeMentionCount id={episode.id} />
        </td>
        <td>
          <TranscriptStatusCell
            episode_id={episode.id}
            transcript_status={episode.latest_transcript_status}
          />
        </td>
      </tr>
      {showDetailsEpisodeID === episode.id && (
        <EpisodeDetails id={episode.id} />
      )}
    </>
  );
};

const EpisodeListTable: React.FunctionComponent<EpisodeListTableProps> = ({
  channel_type,
  episodes,
}) => {
  const [showDetailsEpisodeID, setShowDetailsEpisodeID] = useState<
    string | undefined
  >();

  const toggleShowDetails = (episode_id: string) => {
    if (showDetailsEpisodeID === episode_id) {
      setShowDetailsEpisodeID(undefined);
    } else {
      setShowDetailsEpisodeID(episode_id);
    }
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th colSpan={2}>Episode</th>
            <th>Published</th>
            <th>Indexed</th>
            <th>Number of mentions</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {episodes.map((episode) => (
            <EpisodeRow
              key={episode.id}
              channel_type={channel_type}
              episode={episode}
              toggleShowDetails={toggleShowDetails}
              showDetailsEpisodeID={showDetailsEpisodeID}
            />
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default EpisodeListTable;
